import type { AppLocale } from '@/i18n/config';
import { SUPPORTED_LOCALES } from '@/i18n/config';
import type { ThemeMode } from '@/theme/theme';
import type { Density } from '../density/density';

/**
 * The palette's ACTIONS — the half of the standard's "navigates, finds entities, runs actions"
 * that the first shell release deliberately left out, because "run an action" needed a
 * contribution model and there was no decision on what one looked like.
 *
 * THIS IS THAT MODEL, and it is narrower than a plugin registry on purpose. An action here is
 * something the SHELL owns and that applies on every screen: the display preferences and the
 * session. A module's own actions — "create a study", "reserve a treatment date" — are NOT here
 * and must not be, because each depends on the caller's permissions, on the active organization,
 * and often on which record is open, and every one of those is a gate this file cannot see. The
 * palette would then offer a person an action that 403s, which is worse than not offering it: the
 * rail solved that same problem by resolving gates once and deriving every surface from the result
 * (`resolve-navigation.ts`), and a module-action contribution belongs in that same resolved model
 * rather than in an independent list here.
 *
 * PURE, and given fully-formed labels rather than a translator. The composed strings ("Tema:
 * Chiaro") pair two keys from two different parts of the namespace, so composing them at the call
 * site keeps every key a literal the i18n types can check, and leaves this function testable with
 * a plain object.
 */
export type CommandAction = {
  readonly id: string;
  readonly label: string;
  /** `true` when the action names the state the app is ALREADY in. */
  readonly current: boolean;
  readonly run: () => void;
};

export type CommandActionLabels = {
  readonly theme: Readonly<Record<ThemeMode, string>>;
  readonly density: Readonly<Record<Density, string>>;
  readonly language: Readonly<Record<AppLocale, string>>;
  readonly signOut: string;
};

export type CommandActionState = {
  readonly themeMode: ThemeMode;
  readonly setThemeMode: (mode: ThemeMode) => void;
  readonly density: Density;
  readonly setDensity: (density: Density) => void;
  readonly locale: AppLocale;
  readonly setLocale: (locale: AppLocale) => void;
  readonly signOut: () => void;
};

const THEME_MODES: readonly ThemeMode[] = ['light', 'dark', 'system'];
const DENSITIES: readonly Density[] = ['comfortable', 'compact'];

export function buildCommandActions(
  state: CommandActionState,
  labels: CommandActionLabels,
): readonly CommandAction[] {
  return [
    /*
     * The already-active option is LISTED, not filtered out, and marked `current` instead. Two
     * reasons. Typing "tema" and seeing two of three options is a list that has silently lied
     * about what exists; and the palette is often how someone checks WHICH theme is on, which a
     * missing row cannot answer. Selecting it is a harmless no-op that closes the palette.
     */
    ...THEME_MODES.map((mode) => ({
      id: `theme:${mode}`,
      label: labels.theme[mode],
      current: state.themeMode === mode,
      run: () => state.setThemeMode(mode),
    })),
    ...DENSITIES.map((density) => ({
      id: `density:${density}`,
      label: labels.density[density],
      current: state.density === density,
      run: () => state.setDensity(density),
    })),
    ...SUPPORTED_LOCALES.map((locale) => ({
      id: `language:${locale}`,
      label: labels.language[locale],
      current: state.locale === locale,
      run: () => state.setLocale(locale),
    })),
    /*
     * Last, always, and never near the top of a filtered list by accident: it is the only action
     * here that a mis-aimed Enter cannot be undone by simply choosing the other option. It is
     * reachable by typing, which is the point of it being in the palette at all.
     */
    { id: 'session:sign-out', label: labels.signOut, current: false, run: state.signOut },
  ];
}
